import React, { useState, useEffect, useRef } from 'react';
import type { DocDocument, DocTemplate, DocBrand, DocStatus, LibraryScore, GCConflict, BrandConfig, DocTrack } from '../../../types/docops';
import { C, STATUS_CONFIG, PIPELINE_STAGES, SEVERITY_CONFIG, GEN_MESSAGES, CATEGORY_INFO, FIELD_LABELS } from './docopsConstants';
import { RegenDiagnosticModal } from './RegenDiagnosticModal';
import {
  REVIEWED_STATUSES,
  getDaysInState as getDaysInStatePure,
  filterDocument,
  getDepInfo as getDepInfoPure,
  calculateReadinessScore,
  categorizeAttorneyReadiness,
  isRoundLocked as isRoundLockedPure,
  getDocConflicts,
  calculateEffectiveScore,
  getConfigGaps,
  type ConfigGap,
} from '../../../utils/docopsPipeline';

// ── Entity → Brand Config field mapping ──
const ENTITY_CONFIG_MAP: Record<string, { field: keyof BrandConfig; label: string }> = {
  medicalDirector:        { field: 'medicalDirectorName', label: 'Medical Director Name' },
  professionalCorporation: { field: 'pcEntityName', label: 'PC Entity Name' },
  trustEntity:            { field: 'trustName', label: 'Trust Name' },
  telehealthProvider:     { field: 'telehealthNetwork', label: 'Telehealth Network' },
  paymentProcessor:       { field: 'paymentProcessor', label: 'Payment Processor' },
  operatingCompany:       { field: 'brandEntity', label: 'Brand Entity Name' },
};

interface PipelineViewProps {
  // Data
  documents: DocDocument[];
  templates: DocTemplate[];
  brands: DocBrand[];
  activeBrand: DocBrand | null;
  libraryScore: LibraryScore | null;
  pipelineGroups: Record<string, DocDocument[]>;
  nextRoundInfo: { round: number; docs: DocDocument[]; count: number; totalRounds: number; completedRounds: number } | null;

  // Computed values
  totalDocs: number;
  generatedDocs: DocDocument[];
  blockerDocs: DocDocument[];
  attorneyDocs: DocDocument[];

  // Generation state (from hook)
  generating: boolean;
  generatingAll: boolean;
  genProgress: { current: number; total: number; title: string } | null;
  auditProgress: { attempt: number; maxAttempts: number; phase: 'generating' | 'scoring' | 'fixing'; score?: number; passed?: boolean } | null;
  genError: string | null;
  genMsgIdx: number;

  // Filter state
  searchQuery: string;
  activeFilters: Set<string>;
  collapsedStages: Set<string>;
  showConflicts: boolean;
  saving: boolean;

  // Callbacks
  setActiveBrand: (brand: DocBrand) => void;
  openConfig: () => void;
  openDocument: (docId: string) => void;
  setSearchQuery: (q: string) => void;
  toggleFilter: (filter: string) => void;
  toggleStage: (stageId: string) => void;
  setShowConflicts: (show: boolean) => void;
  generateDocument: (docId: string) => void;
  generateNextRound: (roundInfo: { round: number; docs: DocDocument[]; count: number }) => void;
  generateDocuments: (phase: 'blockers' | 'remaining' | 'all') => void;
  applyConfigToAll: () => void;
  rescoreUngated: () => Promise<number | undefined>;
  rescoreAll: () => Promise<number | undefined>;
  fixConflict: (conflict: GCConflict, configOverrides?: Partial<BrandConfig>) => Promise<void>;
  fixAllConflicts: (conflicts: GCConflict[]) => Promise<void>;
  activeJobId: string | null;
  jobDocIds: Set<string>;
  preRegenScores: Record<string, number>;
  cancelJob: () => Promise<void>;
  fixingConflicts: boolean;
  fixProgress: { current: number; total: number; title: string } | null;
  onUpdateConfigField: (field: keyof BrandConfig, value: string) => Promise<void>;
  bulkUpdateStatus: (docIds: string[], newStatus: DocStatus) => void;
  clearGenError: () => void;
  downloadAllDocs: () => void;
  exporting: boolean;
  regenerateBatch: (docs: DocDocument[]) => void;
  // Track filter
  activeTrack: DocTrack;
  setActiveTrack: (track: DocTrack) => void;
  trackCounts: Record<string, number>;
}

export const PipelineView: React.FC<PipelineViewProps> = ({
  documents,
  templates,
  brands,
  activeBrand,
  libraryScore,
  pipelineGroups,
  nextRoundInfo,
  totalDocs,
  generatedDocs,
  blockerDocs,
  attorneyDocs,
  generating,
  generatingAll,
  genProgress,
  auditProgress,
  genError,
  genMsgIdx,
  searchQuery,
  activeFilters,
  collapsedStages,
  showConflicts,
  saving,
  setActiveBrand,
  openConfig,
  openDocument,
  setSearchQuery,
  toggleFilter,
  toggleStage,
  setShowConflicts,
  generateDocument,
  generateNextRound,
  generateDocuments,
  applyConfigToAll,
  rescoreUngated,
  rescoreAll,
  fixConflict,
  fixAllConflicts,
  activeJobId,
  jobDocIds,
  preRegenScores,
  cancelJob,
  fixingConflicts,
  fixProgress,
  onUpdateConfigField,
  bulkUpdateStatus,
  clearGenError,
  downloadAllDocs,
  exporting,
  regenerateBatch,
  activeTrack,
  setActiveTrack,
  trackCounts,
}) => {
  // ── Config prompt modal state ──
  const [configPrompt, setConfigPrompt] = useState<{
    field: keyof BrandConfig;
    label: string;
    conflict: GCConflict;
    value: string;
  } | null>(null);

  // ── Diagnostic regen modal state ──
  const [diagnosticDoc, setDiagnosticDoc] = useState<DocDocument | null>(null);

  // ── Auto AI Fix after generation ──
  const [pendingAutoFix, setPendingAutoFix] = useState(false);
  const [autoFixAfterGen, setAutoFixAfterGen] = useState(true);
  const [autoFixPass, setAutoFixPass] = useState(0); // track passes to cap at 2
  const prevFixing = useRef(fixingConflicts);

  // ── Readiness panel (collapsed by default) ──
  const [showReadiness, setShowReadiness] = useState(false);

  // ── Regen queue with auto-processing ──
  const [regenQueue, setRegenQueue] = useState<Set<string>>(new Set());
  const prevGeneratingAll = useRef(generatingAll);

  const toggleRegenQueue = (docId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // Warn before selecting attorney-reviewed docs for regen
    const doc = documents.find(d => d.id === docId);
    if (doc && REVIEWED_STATUSES.has(doc.status) && !regenQueue.has(docId)) {
      if (!window.confirm(`"${doc.title}" is in ${STATUS_CONFIG[doc.status].label} status. Adding to regen queue will reset it to DRAFT. Continue?`)) return;
    }
    setRegenQueue(prev => {
      const next = new Set(prev);
      if (next.has(docId)) next.delete(docId);
      else next.add(docId);
      return next;
    });
  };

  // Auto-process queue when generation finishes
  useEffect(() => {
    const wasGenerating = prevGeneratingAll.current;
    prevGeneratingAll.current = generatingAll;

    // Job just started — clear queue (docs are now in the job)
    if (!wasGenerating && generatingAll && regenQueue.size > 0) {
      setRegenQueue(new Set());
    }

    // Generation just finished — trigger auto-fix if enabled
    if (wasGenerating && !generatingAll && autoFixAfterGen) {
      setPendingAutoFix(true);
    }

    // If generation just finished and queue has items, auto-trigger
    if (wasGenerating && !generatingAll && regenQueue.size > 0) {
      const docs = documents.filter(d => regenQueue.has(d.id));
      if (docs.length > 0) {
        console.log(`[Regen] Auto-processing ${docs.length} queued doc(s)`);
        regenerateBatch(docs);
        // Don't clear regenQueue here — cleared when generatingAll becomes true
      }
    }
  }, [generatingAll]);

  // Auto AI Fix: trigger when libraryScore recomputes after generation
  useEffect(() => {
    if (!pendingAutoFix || !libraryScore || fixingConflicts || generatingAll) return;
    const openConflicts = libraryScore.conflicts.filter(c =>
      c.status !== 'pending' &&
      documents.some(d => d.templateId === c.documentA && d.content),
    );
    if (openConflicts.length === 0) { setPendingAutoFix(false); setAutoFixPass(0); return; }
    console.log(`[AutoFix] Pass ${autoFixPass + 1}: fixing ${openConflicts.length} conflict(s)`);
    setPendingAutoFix(false);
    setAutoFixPass(prev => prev + 1);
    fixAllConflicts(openConflicts);
  }, [pendingAutoFix, libraryScore]);

  // After auto-fix completes: second pass if conflicts remain, then auto-regen low-score docs
  useEffect(() => {
    const wasFixing = prevFixing.current;
    prevFixing.current = fixingConflicts;
    if (!wasFixing || fixingConflicts || !autoFixAfterGen || generatingAll) return;

    // Auto-fix just finished — check for remaining conflicts (max 2 passes)
    if (autoFixPass > 0 && autoFixPass < 2 && libraryScore) {
      const remaining = libraryScore.conflicts.filter(c =>
        c.status !== 'pending' &&
        documents.some(d => d.templateId === c.documentA && d.content),
      );
      if (remaining.length > 0) {
        console.log(`[AutoFix] Pass ${autoFixPass} done, ${remaining.length} conflict(s) remain — scheduling pass ${autoFixPass + 1}`);
        setPendingAutoFix(true);
        return;
      }
    }

    // All passes done — auto-regen docs below 50% (they need full regeneration, not patches)
    const regenCandidates = documents.filter(d =>
      d.content && d.status === 'draft' &&
      d.auditScore != null && d.auditScore < 50,
    );
    if (regenCandidates.length > 0) {
      console.log(`[AutoRegen] ${regenCandidates.length} doc(s) below 50% — queuing for regen:`,
        regenCandidates.map(d => `${d.title} (${d.auditScore}%)`));
      regenerateBatch(regenCandidates);
    }
    setAutoFixPass(0);
  }, [fixingConflicts]);

  const handleRegenSelected = () => {
    const docs = documents.filter(d => regenQueue.has(d.id));
    if (docs.length === 0) return;
    if (!window.confirm(`Regenerate ${docs.length} document(s)? This will replace existing content with fresh AI-generated drafts.`)) return;
    regenerateBatch(docs);
    // Don't clear regenQueue here — cleared when generatingAll becomes true
  };

  // ── Cancel feedback ──
  const [cancelling, setCancelling] = useState(false);
  useEffect(() => { if (!generatingAll) setCancelling(false); }, [generatingAll]);

  // ── Compact progress bar when scrolled ──
  const [compactProgress, setCompactProgress] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    // The scrollable container is the <main> parent with overflow-y-auto
    const el = wrapRef.current?.closest('main') ?? wrapRef.current?.closest('[class*="overflow"]');
    if (!el) return;
    const onScroll = () => setCompactProgress(el.scrollTop > 120);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  // ── Collapsed generation rounds (sub-groups within Needs Generation) ──
  const [collapsedRounds, setCollapsedRounds] = useState<Set<number>>(new Set());
  const toggleRound = (round: number) => {
    setCollapsedRounds(prev => {
      const next = new Set(prev);
      if (next.has(round)) next.delete(round);
      else next.add(round);
      return next;
    });
  };

  // ── Collapsed category groups (sub-groups within Attorney Review) ──
  const [collapsedAttyGroups, setCollapsedAttyGroups] = useState<Set<string>>(new Set());
  const toggleAttyGroup = (catId: string) => {
    setCollapsedAttyGroups(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId);
      else next.add(catId);
      return next;
    });
  };

  // ── Pre-flight config check ──
  const [preFlightGaps, setPreFlightGaps] = useState<{
    gaps: ConfigGap[];
    values: Record<string, string>;
    pendingAction: () => void;
  } | null>(null);

  const runWithPreFlight = (
    templateFilter: (t: DocTemplate) => boolean,
    action: () => void,
  ) => {
    if (!activeBrand) return;
    const relevantTemplates = templates.filter(templateFilter);
    const gaps = getConfigGaps(relevantTemplates, activeBrand.config, FIELD_LABELS);
    if (gaps.length === 0) { action(); return; }
    const initialValues: Record<string, string> = {};
    for (const gap of gaps) {
      const current = activeBrand.config[gap.field];
      initialValues[gap.field] = typeof current === 'string' ? current
        : typeof current === 'number' ? String(current) : '';
    }
    setPreFlightGaps({ gaps, values: initialValues, pendingAction: action });
  };

  // ── Fix result feedback ──
  const [fixResult, setFixResult] = useState<string | null>(null);

  /** Always show confirmation modal for entity conflicts (pre-filled if config exists) */
  const handleFixConflict = (conflict: GCConflict) => {
    if (conflict.type === 'entity') {
      // Extract entity ID from conflict.id (format: entity-{entityId}-{docId})
      const parts = conflict.id.split('-');
      const entityId = parts.length >= 2 ? parts[1] : null;
      console.log(`[AIFix] Entity conflict clicked. id=${conflict.id} entityId=${entityId}`);
      if (entityId) {
        const mapping = ENTITY_CONFIG_MAP[entityId];
        if (mapping && activeBrand) {
          const currentVal = activeBrand.config[mapping.field];
          const prefill = (typeof currentVal === 'string' ? currentVal.trim() : '') || '';
          console.log(`[AIFix] Showing modal for ${mapping.label}, prefill="${prefill}"`);
          setConfigPrompt({ field: mapping.field, label: mapping.label, conflict, value: prefill });
          return;
        }
        console.log(`[AIFix] No config mapping for entityId="${entityId}", proceeding directly`);
      }
    }
    // Non-entity conflicts or unmapped entities — proceed directly
    console.log(`[AIFix] Direct fix for conflict type=${conflict.type}`);
    handleDirectFix(conflict);
  };

  /** Submit config value from modal, save to config, then fix with override */
  const handleConfigSubmit = async () => {
    if (!configPrompt || !configPrompt.value.trim()) return;
    const { field, value, conflict } = configPrompt;
    const trimmed = value.trim();
    // Save to brand config (async, state updates on next render)
    await onUpdateConfigField(field, trimmed);
    setConfigPrompt(null);
    setFixResult(null);
    // Pass the new value directly as override so the fix prompt uses it immediately
    try {
      await fixConflict(conflict, { [field]: trimmed } as Partial<BrandConfig>);
      setFixResult(`Fixed: ${conflict.description.slice(0, 80)}`);
      setTimeout(() => setFixResult(null), 5000);
    } catch {
      // Error already set via genError in the hook
    }
  };

  /** Direct fix (non-entity) with feedback */
  const handleDirectFix = async (conflict: GCConflict) => {
    setFixResult(null);
    try {
      await fixConflict(conflict);
      setFixResult(`Fixed: ${conflict.description.slice(0, 80)}`);
      setTimeout(() => setFixResult(null), 5000);
    } catch {
      // Error already set via genError in the hook
    }
  };
  // ── LOCAL HELPERS ──

  const getDaysInState = (doc: DocDocument): number => getDaysInStatePure(doc);

  const filterDoc = (doc: DocDocument): boolean =>
    filterDocument(doc, {
      searchQuery,
      activeFilters,
      allConflicts: libraryScore?.conflicts ?? [],
    });

  const getDepInfo = (doc: DocDocument) => getDepInfoPure(doc, templates);

  const getTemplate = (doc: DocDocument): DocTemplate | undefined =>
    templates.find(t => t.id === doc.templateId);

  // ── COMPUTED VALUES ──

  // Readiness = quality (40%) + generation % (30%) + blocker % (30%)
  const { readinessScore, qualityScore, genPercent, blockerPercent } =
    calculateReadinessScore(libraryScore, totalDocs, generatedDocs.length, documents);
  const readinessColor = readinessScore >= 92 ? C.success : readinessScore >= 70 ? C.amber : C.red;

  // Segmented bar data
  const segments = PIPELINE_STAGES.map(stage => ({
    ...stage,
    count: pipelineGroups[stage.id]?.length ?? 0,
  })).filter(s => s.count > 0);

  return (
    <div ref={wrapRef}>
      {/* Header */}
      <div className="dc-header">
        <div>
          <h1 className="dc-title">Document Operations Center</h1>
          <p className="dc-subtitle">
            {activeBrand ? `${activeBrand.name} \u2014 ${totalDocs} documents${activeTrack !== 'legal' ? ` (${activeTrack})` : ''}` : 'Select a brand to begin'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {brands.length > 0 && (
            <select
              className="dc-select"
              value={activeBrand?.id || ''}
              onChange={(e) => {
                const brand = brands.find(b => b.id === e.target.value);
                if (brand) setActiveBrand(brand);
              }}
            >
              {brands.map(b => (
                <option key={b.id} value={b.id}>{b.name}</option>
              ))}
            </select>
          )}
          <button className="dc-btn dc-btn-secondary" onClick={openConfig} disabled={!activeBrand}>
            Brand Config
          </button>
        </div>
      </div>

      {/* Track Selector */}
      {Object.keys(trackCounts).length > 1 && (
        <div style={{
          display: 'flex', gap: 2, marginBottom: 12,
          borderBottom: `1px solid ${C.borderLight}`, paddingBottom: 1,
        }}>
          {([
            { id: 'legal' as DocTrack, label: 'Legal Pipeline' },
            { id: 'reference' as DocTrack, label: 'Reference' },
            { id: 'operational' as DocTrack, label: 'Operational' },
            { id: 'planning' as DocTrack, label: 'Planning' },
            { id: 'acquisition' as DocTrack, label: 'Acquisition' },
          ] as const).filter(t => (trackCounts[t.id] ?? 0) > 0).map(t => (
            <button
              key={t.id}
              onClick={() => setActiveTrack(t.id)}
              style={{
                padding: '6px 14px',
                fontSize: 12,
                fontWeight: activeTrack === t.id ? 600 : 400,
                color: activeTrack === t.id ? C.accent : C.textMuted,
                background: 'none',
                border: 'none',
                borderBottom: activeTrack === t.id ? `2px solid ${C.accent}` : '2px solid transparent',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                transition: 'color 0.15s, border-color 0.15s',
              }}
            >
              {t.label} ({trackCounts[t.id] ?? 0})
            </button>
          ))}
        </div>
      )}

      {/* Segmented Progress Bar */}
      {totalDocs > 0 && (
        <div className="dc-segment-bar-wrap">
          <div className="dc-segment-bar">
            {segments.map(seg => (
              <div
                key={seg.id}
                className="dc-segment"
                style={{
                  width: `${(seg.count / totalDocs) * 100}%`,
                  background: seg.color,
                }}
                title={`${seg.label}: ${seg.count}`}
              />
            ))}
          </div>
          <div className="dc-segment-legend">
            {segments.map(seg => (
              <span key={seg.id} className="dc-segment-legend-item">
                <span className="dc-dot" style={{ background: seg.color }} />
                {seg.label} ({seg.count})
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Generation Progress (when bulk generating) — sticky at top, compact when scrolled */}
      {generatingAll && genProgress && (
        <div style={{
          position: 'sticky', top: 0, zIndex: 20,
          background: C.blueBg, border: `1px solid ${C.blue}`,
          borderRadius: compactProgress ? 3 : 4,
          padding: compactProgress ? '6px 12px' : 16,
          marginBottom: compactProgress ? 8 : 16,
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          transition: 'padding 0.2s, border-radius 0.2s, margin-bottom 0.2s',
        }}>
          {compactProgress ? (
            /* ── Compact strip ── */
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div className="dc-progress-bar" style={{ height: 6, width: 60, flexShrink: 0 }}>
                <div className="dc-progress-fill" style={{
                  width: `${Math.round((genProgress.current / genProgress.total) * 100)}%`,
                  background: C.blue,
                }} />
              </div>
              <span style={{ fontSize: 12, fontWeight: 600, color: C.blue, whiteSpace: 'nowrap' }}>
                {genProgress.current}/{genProgress.total}
              </span>
              <span style={{ fontSize: 12, color: C.textSec, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {genProgress.title}
                {auditProgress && (
                  <span style={{ color: C.textMuted, marginLeft: 6 }}>
                    {auditProgress.phase === 'generating' && `\u00B7 Drafting...`}
                    {auditProgress.phase === 'scoring' && `\u00B7 Scoring...`}
                    {auditProgress.phase === 'fixing' && `\u00B7 Fixing (${auditProgress.score}%)`}
                  </span>
                )}
              </span>
              {activeJobId && (
                <button
                  onClick={() => { setCancelling(true); cancelJob(); }}
                  disabled={cancelling}
                  style={{
                    fontSize: 10, padding: '2px 8px', borderRadius: 4, flexShrink: 0,
                    background: cancelling ? C.borderLight : C.redBg,
                    color: cancelling ? C.textMuted : C.red,
                    border: `1px solid ${cancelling ? C.borderLight : C.red}`,
                    cursor: cancelling ? 'default' : 'pointer', fontWeight: 600,
                  }}
                >
                  {cancelling ? 'Cancelling...' : 'Cancel'}
                </button>
              )}
            </div>
          ) : (
            /* ── Full banner ── */
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: C.blue }}>
                  Generating {genProgress.current} of {genProgress.total}: {genProgress.title}
                </div>
                {activeJobId && (
                  <button
                    onClick={() => { setCancelling(true); cancelJob(); }}
                    disabled={cancelling}
                    style={{
                      fontSize: 11, padding: '3px 10px', borderRadius: 6,
                      background: cancelling ? C.borderLight : C.redBg,
                      color: cancelling ? C.textMuted : C.red,
                      border: `1px solid ${cancelling ? C.borderLight : C.red}`,
                      cursor: cancelling ? 'default' : 'pointer', fontWeight: 600,
                    }}
                  >
                    {cancelling ? 'Cancelling...' : 'Cancel'}
                  </button>
                )}
              </div>
              <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 8 }}>
                {GEN_MESSAGES[genMsgIdx]}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: auditProgress ? 12 : 0 }}>
                <div className="dc-progress-bar" style={{ height: 10, flex: 1 }}>
                  <div className="dc-progress-fill" style={{
                    width: `${Math.round((genProgress.current / genProgress.total) * 100)}%`,
                    background: C.blue,
                  }} />
                </div>
                <span style={{ fontSize: 11, fontWeight: 600, color: C.blue, whiteSpace: 'nowrap' }}>
                  {genProgress.current}/{genProgress.total}
                </span>
              </div>
              {auditProgress && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: auditProgress.passed ? C.success : auditProgress.score !== undefined ? C.amber : C.textSec }}>
                      {auditProgress.phase === 'generating' && `Attempt ${auditProgress.attempt}/${auditProgress.maxAttempts} \u2014 Drafting...`}
                      {auditProgress.phase === 'scoring' && `Attempt ${auditProgress.attempt}/${auditProgress.maxAttempts} \u2014 Scoring...`}
                      {auditProgress.phase === 'fixing' && `Attempt ${auditProgress.attempt}/${auditProgress.maxAttempts} \u2014 Fixing (scored ${auditProgress.score}%)...`}
                    </div>
                    {auditProgress.score !== undefined && (
                      <div style={{ fontSize: 12, fontWeight: 700, color: auditProgress.passed ? C.success : auditProgress.score >= 80 ? C.amber : C.red }}>
                        {auditProgress.passed ? `${auditProgress.score}% PASS` : `${auditProgress.score}%`}
                      </div>
                    )}
                  </div>
                  <div className="dc-progress-bar" style={{ height: 8 }}>
                    <div className={`dc-progress-fill${auditProgress.phase === 'generating' ? ' dc-progress-pulse' : ''}`} style={{
                      width: auditProgress.phase === 'generating' ? '60%'
                        : auditProgress.phase === 'scoring' ? '85%'
                        : auditProgress.passed ? '100%'
                        : `${Math.round(((auditProgress.attempt - 1) / auditProgress.maxAttempts) * 100 + (100 / auditProgress.maxAttempts) * 0.6)}%`,
                      background: auditProgress.passed ? C.success : auditProgress.score !== undefined ? C.amber : C.accent,
                    }} />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* AI Fix Progress (auto or manual) */}
      {fixingConflicts && fixProgress && (
        <div style={{
          background: C.amberBg, border: `1px solid ${C.amber}`,
          borderRadius: 4, padding: 14, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: C.amber }}>
              {pendingAutoFix || autoFixAfterGen ? 'Auto-fixing' : 'Fixing'} conflict {fixProgress.current} of {fixProgress.total}
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.textSec, marginBottom: 8 }}>
            {fixProgress.title}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div className="dc-progress-bar" style={{ height: 8, flex: 1 }}>
              <div className="dc-progress-fill" style={{
                width: `${Math.round((fixProgress.current / fixProgress.total) * 100)}%`,
                background: C.amber,
              }} />
            </div>
            <span style={{ fontSize: 11, fontWeight: 600, color: C.amber, whiteSpace: 'nowrap' }}>
              {fixProgress.current}/{fixProgress.total}
            </span>
          </div>
        </div>
      )}

      {/* Fix Success */}
      {fixResult && (
        <div style={{
          background: C.successBg, border: `1px solid ${C.success}`, borderRadius: 4,
          padding: 14, marginBottom: 16, fontSize: 13, color: C.success,
        }}>
          <strong>Success:</strong> {fixResult}
          <button onClick={() => setFixResult(null)}
            style={{ marginLeft: 12, background: 'none', border: 'none', color: C.success, cursor: 'pointer', fontWeight: 600 }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Generation Error */}
      {genError && (
        <div style={{
          background: C.redBg, border: `1px solid ${C.red}`, borderRadius: 4,
          padding: 14, marginBottom: 16, fontSize: 13, color: C.red, whiteSpace: 'pre-wrap',
        }}>
          <strong>Note:</strong> {genError}
          <button onClick={() => clearGenError()}
            style={{ marginLeft: 12, background: 'none', border: 'none', color: C.red, cursor: 'pointer', fontWeight: 600 }}>
            Dismiss
          </button>
        </div>
      )}

      {/* Library Score Breakdown — auto-hidden below 80% readiness, togglable via toolbar link */}
      {libraryScore && libraryScore.generatedDocuments > 0 && (readinessScore >= 80 || showReadiness) && (() => {
        const isOpen = showReadiness;
        return (
          <div className="dc-readiness" style={{ padding: isOpen ? undefined : '10px 16px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}
              onClick={() => setShowReadiness(prev => !prev)}
            >
              <div className="dc-readiness-title" style={{ marginBottom: 0 }}>
                Brand Legal Readiness
                <span style={{ fontWeight: 400, color: C.textMuted, marginLeft: 8, fontSize: 12 }}>
                  {generatedDocs.length}/{totalDocs} generated | {libraryScore.generatedDocuments} scored
                </span>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{
                  fontSize: 22, fontWeight: 800,
                  color: readinessColor,
                }}>
                  {readinessScore}%
                  <span style={{ fontSize: 11, fontWeight: 500, marginLeft: 6, color: C.textMuted }}>
                    {readinessScore >= 92 && libraryScore.criticalConflicts === 0 ? 'LAUNCH READY' : 'NOT READY'}
                  </span>
                </div>
                <span style={{ fontSize: 10, color: C.textMuted }}>{isOpen ? '\u25B2' : '\u25BC'}</span>
              </div>
            </div>

            {isOpen && (
              <>
                {/* Scorecard */}
                <div className="dc-scorecard" style={{ marginTop: 12, marginBottom: 12 }}>
                  <div className="dc-metric">
                    <div className="dc-metric-value" style={{ color: C.text }}>{totalDocs}</div>
                    <div className="dc-metric-label">Total Docs</div>
                  </div>
                  <div className="dc-metric">
                    <div className="dc-metric-value" style={{ color: C.success }}>{generatedDocs.length}</div>
                    <div className="dc-metric-label">Generated</div>
                  </div>
                  <div className="dc-metric">
                    <div className="dc-metric-value" style={{ color: blockerDocs.length > 0 ? C.red : C.success }}>
                      {blockerDocs.length}
                    </div>
                    <div className="dc-metric-label">Blockers Left</div>
                  </div>
                  <div className="dc-metric">
                    <div className="dc-metric-value" style={{ color: attorneyDocs.length > 0 ? C.orange : C.text }}>
                      {attorneyDocs.length}
                    </div>
                    <div className="dc-metric-label">Atty Queue</div>
                  </div>
                  <div className="dc-metric">
                    <div className="dc-metric-value" style={{ color: readinessColor }}>
                      {readinessScore}%
                    </div>
                    <div className="dc-metric-label">Readiness</div>
                  </div>
                </div>

                <div className="dc-progress-bar" style={{ marginBottom: 12 }}>
                  <div className="dc-progress-fill" style={{
                    width: `${readinessScore}%`,
                    background: readinessScore >= 92
                      ? `linear-gradient(90deg, ${C.success}, #34D399)`
                      : readinessScore >= 70
                        ? `linear-gradient(90deg, ${C.amber}, #FBBF24)`
                        : `linear-gradient(90deg, ${C.red}, #F87171)`,
                  }} />
                  <div className="dc-progress-label">{readinessScore}%</div>
                </div>

                {/* Readiness components -- 3-part formula */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                  <div style={{ background: C.bg, borderRadius: 3, padding: '8px 10px', textAlign: 'center', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: qualityScore >= 90 ? C.success : qualityScore >= 70 ? C.amber : C.red }}>{qualityScore}%</div>
                    <div style={{ fontSize: 10, color: C.textMuted }}>Quality (40%)</div>
                  </div>
                  <div style={{ background: C.bg, borderRadius: 3, padding: '8px 10px', textAlign: 'center', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: genPercent >= 90 ? C.success : genPercent >= 50 ? C.amber : C.red }}>{genPercent}%</div>
                    <div style={{ fontSize: 10, color: C.textMuted }}>Generated (30%)</div>
                  </div>
                  <div style={{ background: C.bg, borderRadius: 3, padding: '8px 10px', textAlign: 'center', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 18, fontWeight: 700, color: blockerPercent >= 90 ? C.success : blockerPercent >= 50 ? C.amber : C.red }}>{blockerPercent}%</div>
                    <div style={{ fontSize: 10, color: C.textMuted }}>Blockers (30%)</div>
                  </div>
                </div>

                {/* Quality breakdown */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8, marginBottom: 8 }}>
                  <div style={{ background: C.bg, borderRadius: 3, padding: '6px 10px', textAlign: 'center', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{libraryScore.avgIndividualScore}%</div>
                    <div style={{ fontSize: 9, color: C.textMuted }}>Avg Doc Score</div>
                  </div>
                  <div style={{ background: C.bg, borderRadius: 3, padding: '6px 10px', textAlign: 'center', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text }}>{libraryScore.crossDocScore.overallConsistency}%</div>
                    <div style={{ fontSize: 9, color: C.textMuted }}>Cross-Doc</div>
                  </div>
                  <div style={{ background: C.bg, borderRadius: 3, padding: '6px 10px', textAlign: 'center', border: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 14, fontWeight: 700, color: libraryScore.criticalConflicts > 0 ? C.red : C.success }}>
                      {libraryScore.criticalConflicts}
                    </div>
                    <div style={{ fontSize: 9, color: C.textMuted }}>Real Conflicts</div>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 4 }}>
                  {[
                    { label: 'Entity Consistency', value: libraryScore.crossDocScore.entityConsistency },
                    { label: 'Defined Terms', value: libraryScore.crossDocScore.definedTermConsistency },
                    { label: 'Provision Alignment', value: libraryScore.crossDocScore.provisionAlignment },
                    { label: 'Cross-References', value: libraryScore.crossDocScore.crossReferenceIntegrity },
                    { label: 'Regulatory', value: libraryScore.crossDocScore.regulatoryCompleteness },
                    { label: 'Temporal', value: libraryScore.crossDocScore.temporalConsistency },
                  ].map(item => (
                    <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0' }}>
                      <span style={{ fontSize: 11, color: C.textMuted }}>{item.label}</span>
                      <span style={{
                        fontSize: 11, fontWeight: 600,
                        color: item.value >= 90 ? C.success : item.value >= 70 ? C.amber : C.red,
                      }}>
                        {item.value}%
                      </span>
                    </div>
                  ))}
                </div>

                <div style={{ fontSize: 10, color: C.textMuted, marginTop: 8, textAlign: 'right' }}>
                  Readiness = Quality 40% + Generated 30% + Blockers 30% | Launch: 92%+ and zero real conflicts
                </div>
              </>
            )}
          </div>
        );
      })()}

      {/* Search & Filter Toolbar */}
      <div className="dc-toolbar">
        <div className="dc-search-wrap">
          <span className="dc-search-icon">{'\uD83D\uDD0D'}</span>
          <input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="dc-search-input"
          />
          {searchQuery && (
            <button className="dc-search-clear" onClick={() => setSearchQuery('')}>{'\u2715'}</button>
          )}
        </div>
        <div className="dc-filter-tags">
          {[
            { id: 'blocker', label: 'Launch Blockers', color: C.red },
            { id: 'attorney', label: 'Atty Required', color: C.orange },
            { id: 'conflicts', label: 'Has Conflicts', color: C.amber },
            { id: 'overdue', label: 'Overdue (14d+)', color: C.red },
          ].map(f => (
            <button
              key={f.id}
              className={`dc-filter-tag ${activeFilters.has(f.id) ? 'dc-filter-tag-active' : ''}`}
              style={activeFilters.has(f.id) ? { background: f.color, borderColor: f.color, color: '#fff' } : {}}
              onClick={() => toggleFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
        <div className="dc-toolbar-actions">
          {nextRoundInfo && (
            <button
              className="dc-btn dc-btn-primary"
              onClick={() => {
                if (!nextRoundInfo) return;
                const roundDocIds = new Set(nextRoundInfo.docs.map(d => d.templateId));
                runWithPreFlight(
                  t => roundDocIds.has(t.id),
                  () => generateNextRound(nextRoundInfo),
                );
              }}
              disabled={!activeBrand || generatingAll}
              style={{ background: C.accent }}
            >
              {generatingAll ? 'Working...' : `Generate Round ${nextRoundInfo.round} (${nextRoundInfo.count} docs)`}
            </button>
          )}
          <button
            className="dc-btn dc-btn-secondary"
            onClick={() => {
              const notStartedBlockers = documents.filter(d => d.status === 'not_started' && d.launchBlocker);
              const ids = new Set(notStartedBlockers.map(d => d.templateId));
              runWithPreFlight(t => ids.has(t.id), () => generateDocuments('blockers'));
            }}
            disabled={!activeBrand || generatingAll || documents.filter(d => d.status === 'not_started' && d.launchBlocker).length === 0}
          >
            {`Blockers (${documents.filter(d => d.status === 'not_started' && d.launchBlocker).length})`}
          </button>
          <button
            className="dc-btn dc-btn-secondary"
            onClick={() => {
              const notStartedRemaining = documents.filter(d => d.status === 'not_started' && !d.launchBlocker);
              const ids = new Set(notStartedRemaining.map(d => d.templateId));
              runWithPreFlight(t => ids.has(t.id), () => generateDocuments('remaining'));
            }}
            disabled={!activeBrand || generatingAll || documents.filter(d => d.status === 'not_started' && !d.launchBlocker).length === 0}
          >
            {`Remaining (${documents.filter(d => d.status === 'not_started' && !d.launchBlocker).length})`}
          </button>
          <button
            className="dc-btn dc-btn-secondary"
            onClick={applyConfigToAll}
            disabled={!activeBrand}
            style={{ borderColor: C.success, color: C.success }}
          >
            Apply Config
          </button>
          {documents.some(d => d.content && d.status !== 'not_started' && d.auditScore === undefined) && (
            <button
              className="dc-btn dc-btn-secondary"
              onClick={() => rescoreUngated()}
              disabled={!activeBrand}
              style={{ borderColor: C.blue, color: C.blue }}
            >
              {`Score Unrated (${documents.filter(d => d.content && d.status !== 'not_started' && d.auditScore === undefined).length})`}
            </button>
          )}
          {generatedDocs.length > 0 && (
            <button
              className="dc-btn dc-btn-secondary"
              onClick={() => {
                if (window.confirm(`Rescore all ${generatedDocs.length} generated documents? This will re-evaluate every document using current quality gates (including any newly added gates).`)) {
                  rescoreAll();
                }
              }}
              disabled={!activeBrand}
              style={{ borderColor: C.accent, color: C.accent }}
            >
              Rescore All ({generatedDocs.length})
            </button>
          )}
          {regenQueue.size > 0 && (
            <button
              className="dc-btn dc-btn-primary"
              onClick={handleRegenSelected}
              disabled={generatingAll || generating}
              style={{ background: C.red }}
            >
              {generatingAll ? 'Working...' : `Regen Selected (${regenQueue.size})`}
            </button>
          )}
          {regenQueue.size > 0 && (
            <button
              className="dc-btn dc-btn-secondary"
              onClick={() => setRegenQueue(new Set())}
              style={{ fontSize: 11, padding: '4px 10px' }}
            >
              Clear
            </button>
          )}
          {generatedDocs.length > 0 && (
            <button
              className="dc-btn dc-btn-secondary"
              onClick={downloadAllDocs}
              disabled={exporting || generatingAll}
              style={{ borderColor: '#6b5b95', color: '#6b5b95' }}
            >
              {exporting ? 'Exporting...' : `Download All (${generatedDocs.length})`}
            </button>
          )}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: 11, color: C.textMuted, cursor: 'pointer', whiteSpace: 'nowrap',
          }}>
            <input
              type="checkbox"
              checked={autoFixAfterGen}
              onChange={e => setAutoFixAfterGen(e.target.checked)}
              style={{ accentColor: C.amber }}
            />
            Auto-fix after gen
          </label>
          {libraryScore && libraryScore.generatedDocuments > 0 && readinessScore < 80 && (
            <button
              onClick={() => setShowReadiness(prev => !prev)}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                fontSize: 11, color: readinessColor, fontWeight: 600,
                whiteSpace: 'nowrap', padding: '2px 6px',
              }}
            >
              {showReadiness ? 'Hide' : 'Readiness'}: {readinessScore}%
            </button>
          )}
        </div>
        {/* Round progress indicator */}
        {nextRoundInfo && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            padding: '8px 12px', background: C.bgSoft, borderRadius: 3,
            border: `1px solid ${C.border}`, fontSize: 12, color: C.textSec,
          }}>
            <span style={{ fontWeight: 600, color: C.text }}>Generation Rounds:</span>
            {Array.from({ length: nextRoundInfo.totalRounds }, (_, i) => i + 1).map(r => {
              const roundDocs = documents.filter(d => {
                const t = templates.find(x => x.id === d.templateId);
                return (t?.generationRound ?? 6) === r;
              });
              const allGenerated = roundDocs.length > 0 && roundDocs.every(d => d.status !== 'not_started');
              const isCurrent = r === nextRoundInfo.round;
              return (
                <span
                  key={r}
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    width: 28, height: 28, borderRadius: '50%', fontSize: 12, fontWeight: 700,
                    background: allGenerated ? C.successBg : isCurrent ? C.accentSoft : C.borderLight,
                    color: allGenerated ? C.success : isCurrent ? C.accent : C.textMuted,
                    border: isCurrent ? `2px solid ${C.accent}` : '2px solid transparent',
                  }}
                  title={`Round ${r}: ${roundDocs.length} docs${allGenerated ? ' (complete)' : isCurrent ? ' (next)' : ''}`}
                >
                  {allGenerated ? '\u2713' : r}
                </span>
              );
            })}
            <span style={{ color: C.textMuted, marginLeft: 4 }}>
              {nextRoundInfo.completedRounds}/{nextRoundInfo.totalRounds} complete
            </span>
          </div>
        )}
      </div>

      {/* Pipeline Stages */}
      <div className="dc-pipeline">
        {PIPELINE_STAGES.map(stage => {
          const stageDocs = (pipelineGroups[stage.id] || []).filter(filterDoc);
          const allStageDocs = pipelineGroups[stage.id] || [];
          const isCollapsed = collapsedStages.has(stage.id);
          const hasFilteredOut = stageDocs.length < allStageDocs.length;
          const isGenStage = stage.id === 'needs_generation';

          return (
            <div key={stage.id} className="dc-stage">
              <button
                className="dc-stage-header"
                onClick={() => toggleStage(stage.id)}
              >
                <span className="dc-dot" style={{ background: stage.dot, width: 10, height: 10 }} />
                <span className="dc-stage-label">{stage.label}</span>
                <span className="dc-stage-count" style={{ color: stage.color }}>
                  {stageDocs.length}
                  {hasFilteredOut && <span style={{ color: C.textMuted, fontWeight: 400 }}> / {allStageDocs.length}</span>}
                </span>
                <span className="dc-stage-desc">{stage.description}</span>
                <span className="dc-chevron">{isCollapsed ? '\u25BC' : '\u25B2'}</span>
              </button>

              {!isCollapsed && (
                <div className="dc-stage-body">
                  {/* Stage action buttons */}
                  {stageDocs.length > 0 && stage.id === 'ready_for_review' && (() => {
                    const { attyReady, conflictHeld, depHeld, lowScore } = categorizeAttorneyReadiness(
                      stageDocs, libraryScore?.conflicts ?? [], templates, documents,
                    );
                    return (
                      <div className="dc-stage-actions" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                        {/* GC Readiness Summary Grid */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
                          <div style={{
                            background: C.successBg, borderRadius: 3, padding: '8px 10px',
                            textAlign: 'center', border: `1px solid ${C.success}30`,
                          }}>
                            <div style={{ fontSize: 20, fontWeight: 700, color: C.success }}>{attyReady.length}</div>
                            <div style={{ fontSize: 9, color: C.success, fontWeight: 600 }}>Ready for Attorney</div>
                          </div>
                          <div style={{
                            background: conflictHeld.length > 0 ? C.redBg : C.bgSoft, borderRadius: 3, padding: '8px 10px',
                            textAlign: 'center', border: `1px solid ${conflictHeld.length > 0 ? C.red : C.border}30`,
                          }}>
                            <div style={{ fontSize: 20, fontWeight: 700, color: conflictHeld.length > 0 ? C.red : C.textMuted }}>{conflictHeld.length}</div>
                            <div style={{ fontSize: 9, color: conflictHeld.length > 0 ? C.red : C.textMuted, fontWeight: 600 }}>Conflict-Held</div>
                          </div>
                          <div style={{
                            background: depHeld.length > 0 ? C.amberBg : C.bgSoft, borderRadius: 3, padding: '8px 10px',
                            textAlign: 'center', border: `1px solid ${depHeld.length > 0 ? C.amber : C.border}30`,
                          }}>
                            <div style={{ fontSize: 20, fontWeight: 700, color: depHeld.length > 0 ? C.amber : C.textMuted }}>{depHeld.length}</div>
                            <div style={{ fontSize: 9, color: depHeld.length > 0 ? C.amber : C.textMuted, fontWeight: 600 }}>Dep-Held</div>
                          </div>
                          <div style={{
                            background: lowScore.length > 0 ? C.bgSoft : C.bgSoft, borderRadius: 3, padding: '8px 10px',
                            textAlign: 'center', border: `1px solid ${C.border}30`,
                          }}>
                            <div style={{ fontSize: 20, fontWeight: 700, color: lowScore.length > 0 ? C.textSec : C.textMuted }}>{lowScore.length}</div>
                            <div style={{ fontSize: 9, color: C.textMuted, fontWeight: 600 }}>Below 95%</div>
                          </div>
                        </div>
                        {/* Action buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className="dc-btn dc-btn-primary"
                            style={{ background: C.orange, fontSize: 12, padding: '5px 14px' }}
                            disabled={saving || attyReady.length === 0}
                            onClick={() => {
                              if (!window.confirm(`Move ${attyReady.length} document(s) to Attorney Review?\n\nAll scored 95%+, no conflicts, dependencies met.`)) return;
                              bulkUpdateStatus(attyReady.map(d => d.id), 'attorney_review');
                            }}
                          >
                            {saving ? 'Updating...' : `Send ${attyReady.length} to Attorney Review`}
                          </button>
                          {lowScore.length > 0 && (
                            <button
                              className="dc-btn"
                              style={{ fontSize: 12, padding: '5px 14px', background: C.amberBg, color: C.amber, border: `1px solid ${C.amber}40` }}
                              disabled={generatingAll || generating}
                              onClick={() => {
                                if (!window.confirm(`Regenerate ${lowScore.length} document(s) scoring below 95%?\n\n${lowScore.map(d => `${d.title} (${d.auditScore ?? 0}%)`).join('\n')}`)) return;
                                regenerateBatch(lowScore);
                              }}
                            >
                              {generatingAll ? 'Working...' : `Regen Below 95% (${lowScore.length})`}
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  {stageDocs.length > 0 && stage.id === 'attorney_review' && (() => {
                    // Only critical/high conflicts warrant moving docs back; medium/low are informational
                    const seriousConflicts = libraryScore?.conflicts.filter(
                      c => c.status !== 'pending' && (c.severity === 'critical' || c.severity === 'high'),
                    ) ?? [];
                    const conflictedDocs = stageDocs.filter(d =>
                      seriousConflicts.some(c => c.documentA === d.templateId || c.documentB === d.templateId),
                    );
                    const cleanDocs = stageDocs.filter(d =>
                      !seriousConflicts.some(c => c.documentA === d.templateId || c.documentB === d.templateId),
                    );
                    return (
                      <div className="dc-stage-actions" style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <button
                            className="dc-btn dc-btn-primary"
                            style={{ background: C.success, fontSize: 12, padding: '5px 14px' }}
                            disabled={saving || cleanDocs.length === 0}
                            onClick={() => {
                              if (!window.confirm(`Mark ${cleanDocs.length} conflict-free document(s) as Attorney Approved?`)) return;
                              bulkUpdateStatus(cleanDocs.map(d => d.id), 'attorney_approved');
                            }}
                          >
                            {saving ? 'Updating...' : `Approve All (${cleanDocs.length})`}
                          </button>
                        </div>
                        {conflictedDocs.length > 0 && (
                          <div style={{
                            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap',
                            padding: '8px 12px', background: C.redBg, borderRadius: 3,
                            border: `1px solid ${C.red}20`,
                          }}>
                            <span style={{ fontSize: 12, color: C.red, fontWeight: 600 }}>
                              {conflictedDocs.length} doc(s) have unresolved conflicts
                            </span>
                            <button
                              className="dc-btn dc-btn-secondary"
                              style={{ fontSize: 11, padding: '3px 10px', borderColor: C.red, color: C.red }}
                              disabled={saving}
                              onClick={() => {
                                if (!window.confirm(`Move ${conflictedDocs.length} conflicted doc(s) back to Ready for Review?\n\n${conflictedDocs.map(d => d.title).join('\n')}`)) return;
                                bulkUpdateStatus(conflictedDocs.map(d => d.id), 'draft');
                              }}
                            >
                              {saving ? 'Moving...' : 'Move Back to Review'}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {stageDocs.length > 0 && stage.id === 'approved' && (
                    <div className="dc-stage-actions" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <button
                        className="dc-btn dc-btn-secondary"
                        style={{ borderColor: C.orange, color: C.orange, fontSize: 12, padding: '5px 14px' }}
                        disabled={saving}
                        onClick={() => {
                          if (!window.confirm(`Move all ${stageDocs.length} document(s) back to Attorney Review?`)) return;
                          bulkUpdateStatus(stageDocs.map(d => d.id), 'attorney_review');
                        }}
                      >
                        {saving ? 'Updating...' : `Move All Back to Attorney Review (${stageDocs.length})`}
                      </button>
                    </div>
                  )}

                  {stageDocs.length === 0 ? (
                    <div className="dc-stage-empty">
                      {allStageDocs.length === 0 ? 'No documents in this stage' : 'No documents match your filters'}
                    </div>
                  ) : (
                    <div className="dc-doc-list">
                      {/* For gen stage: sort by round and inject sub-group headers */}
                      {(() => {
                        const ROUND_LABELS: Record<number, string> = {
                          1: 'Foundation', 2: 'Core Agreements', 3: 'Healthcare Compliance',
                          4: 'Operations', 5: 'Financial & Insurance', 6: 'Board & Governance', 99: 'Unassigned',
                        };
                        const isAttyStage = stage.id === 'attorney_review';
                        const sorted = isGenStage
                          ? [...stageDocs].sort((a, b) => {
                              const ra = templates.find(t => t.id === a.templateId)?.generationRound ?? 99;
                              const rb = templates.find(t => t.id === b.templateId)?.generationRound ?? 99;
                              return ra - rb;
                            })
                          : isAttyStage
                          ? [...stageDocs].sort((a, b) => {
                              const ca = CATEGORY_INFO[a.categoryId]?.order ?? 99;
                              const cb = CATEGORY_INFO[b.categoryId]?.order ?? 99;
                              return ca - cb;
                            })
                          : stageDocs;

                        let prevRound = -1;
                        let prevCat = '';
                        return sorted.flatMap(doc => {
                          const roundNum = isGenStage
                            ? (templates.find(t => t.id === doc.templateId)?.generationRound ?? 99)
                            : -1;
                          const elements: React.ReactNode[] = [];

                          // Inject round sub-header when round changes
                          if (isGenStage && roundNum !== prevRound) {
                            const roundDocs = sorted.filter(d => {
                              const r = templates.find(t => t.id === d.templateId)?.generationRound ?? 99;
                              return r === roundNum;
                            });
                            prevRound = roundNum;
                            const isRoundCollapsed = collapsedRounds.has(roundNum);
                            const lock = isGenStage ? isRoundLockedPure(roundNum, documents, templates) : { locked: false, reason: '' };
                            elements.push(
                              <div
                                key={`round-${roundNum}`}
                                style={{
                                  display: 'flex', alignItems: 'center', gap: 8,
                                  padding: '8px 10px', margin: '4px 0 2px',
                                  background: lock.locked ? `${C.amber}10` : C.borderLight, borderRadius: 3,
                                  cursor: 'pointer', userSelect: 'none',
                                  opacity: lock.locked ? 0.7 : 1,
                                }}
                                onClick={() => toggleRound(roundNum)}
                              >
                                <span style={{ fontSize: 10, color: C.textMuted, width: 12 }}>
                                  {isRoundCollapsed ? '\u25B6' : '\u25BC'}
                                </span>
                                <span style={{ fontSize: 12, fontWeight: 700, color: lock.locked ? C.amber : C.textSec }}>
                                  {lock.locked ? '\uD83D\uDD12 ' : ''}Round {roundNum}
                                </span>
                                <span style={{ fontSize: 11, color: C.textMuted, flex: 1 }}>
                                  {lock.locked ? lock.reason : `${ROUND_LABELS[roundNum] ?? ''} (${roundDocs.length})`}
                                </span>
                                {!lock.locked && (
                                  <button
                                    className="dc-btn dc-btn-primary"
                                    style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6 }}
                                    disabled={generating || generatingAll}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      const roundTplIds = new Set(roundDocs.map(d => d.templateId));
                                      runWithPreFlight(
                                        t => roundTplIds.has(t.id),
                                        () => regenerateBatch(roundDocs),
                                      );
                                    }}
                                  >
                                    {generatingAll ? '...' : `Generate (${roundDocs.length})`}
                                  </button>
                                )}
                              </div>,
                            );
                          }

                          // Inject category sub-header when category changes (attorney review)
                          if (isAttyStage) {
                            const catId = doc.categoryId || 'unknown';
                            if (catId !== prevCat) {
                              const catDocs = sorted.filter(d => (d.categoryId || 'unknown') === catId);
                              prevCat = catId;
                              const catInfo = CATEGORY_INFO[catId] || { name: catId, icon: '', color: C.textMuted, order: 99 };
                              const isCatCollapsed = collapsedAttyGroups.has(catId);
                              elements.push(
                                <div
                                  key={`cat-${catId}`}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: 8,
                                    padding: '8px 10px', margin: '4px 0 2px',
                                    background: `${catInfo.color}10`, borderRadius: 3,
                                    borderLeft: `3px solid ${catInfo.color}`,
                                    cursor: 'pointer', userSelect: 'none',
                                  }}
                                  onClick={() => toggleAttyGroup(catId)}
                                >
                                  <span style={{ fontSize: 10, color: C.textMuted, width: 12 }}>
                                    {isCatCollapsed ? '\u25B6' : '\u25BC'}
                                  </span>
                                  <span style={{ fontSize: 14 }}>{catInfo.icon}</span>
                                  <span style={{ fontSize: 12, fontWeight: 700, color: catInfo.color }}>
                                    {catInfo.name}
                                  </span>
                                  <span style={{ fontSize: 11, color: C.textMuted }}>
                                    ({catDocs.length})
                                  </span>
                                  <span style={{ flex: 1 }} />
                                  <button
                                    className="dc-btn dc-btn-primary"
                                    style={{ fontSize: 10, padding: '3px 10px', borderRadius: 6, background: C.success }}
                                    disabled={saving}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!window.confirm(`Approve all ${catDocs.length} doc(s) in ${catInfo.name}?`)) return;
                                      bulkUpdateStatus(catDocs.map(d => d.id), 'attorney_approved');
                                    }}
                                  >
                                    Approve Group ({catDocs.length})
                                  </button>
                                </div>,
                              );
                            }
                            if (collapsedAttyGroups.has(doc.categoryId || 'unknown')) return elements;
                          }

                          // Skip doc rendering if round is collapsed
                          if (isGenStage && collapsedRounds.has(roundNum)) return elements;

                          const sc = STATUS_CONFIG[doc.status];
                          const depInfo = getDepInfo(doc);
                          const days = getDaysInState(doc);
                          const tpl = getTemplate(doc);
                          elements.push((() => {
                        // Conflicts involving this doc
                        const docConflicts = getDocConflicts(libraryScore?.conflicts ?? [], doc.templateId);
                        const realDocConflicts = getDocConflicts(libraryScore?.conflicts ?? [], doc.templateId, { excludePending: true });
                        const critCount = docConflicts.filter(c => c.severity === 'critical').length;
                        const highCount = docConflicts.filter(c => c.severity === 'high').length;
                        const medCount = realDocConflicts.filter(c => c.severity === 'medium').length;
                        const effectiveScore = calculateEffectiveScore(doc.auditScore, realDocConflicts);

                        return (
                          <div
                            key={doc.id}
                            className="dc-doc-row"
                            onClick={() => openDocument(doc.id)}
                          >
                            <div className="dc-doc-main">
                              {/* Regen checkbox for generated docs (hidden in attorney review) */}
                              {doc.status !== 'not_started' && doc.content && !isAttyStage && (
                                <input
                                  type="checkbox"
                                  checked={regenQueue.has(doc.id)}
                                  onClick={(e) => toggleRegenQueue(doc.id, e)}
                                  onChange={() => {}}
                                  style={{ marginRight: 6, cursor: 'pointer', accentColor: C.red }}
                                  title="Select for regeneration"
                                />
                              )}
                              <span className="dc-doc-title">{doc.title}</span>
                              <div className="dc-doc-tags">
                                {doc.launchBlocker && (
                                  <span className="dc-tag dc-tag-blocker">Blocker</span>
                                )}
                                {/* Show Atty/Gate/Round only in needs_generation */}
                                {isGenStage && doc.attorneyReviewRequired && (
                                  <span className="dc-tag dc-tag-attorney">Atty</span>
                                )}
                                {isGenStage && tpl?.qualityGate && (
                                  <span className="dc-tag dc-tag-gate">Gate</span>
                                )}
                                {isGenStage && tpl?.generationRound && (
                                  <span className="dc-tag" style={{ background: C.blueBg, color: C.blue, fontSize: 9 }}>
                                    Round {tpl.generationRound}
                                  </span>
                                )}
                                {/* Ready for Review: show dep readiness */}
                                {stage.id === 'ready_for_review' && doc.attorneyReviewRequired && (() => {
                                  const docTpl = templates.find(t => t.id === doc.templateId);
                                  const missingDeps = (docTpl?.dependencies ?? []).filter(depId => {
                                    const depDoc = documents.find(x => x.templateId === depId);
                                    return !depDoc || depDoc.status === 'not_started' || depDoc.status === 'generating';
                                  });
                                  if (missingDeps.length > 0) {
                                    const names = missingDeps.map(id => templates.find(t => t.id === id)?.title ?? id);
                                    return (
                                      <span className="dc-tag" style={{ background: C.amberBg, color: C.amber, fontSize: 9 }}>
                                        Waiting: {names.slice(0, 2).join(', ')}{names.length > 2 ? ` +${names.length - 2}` : ''}
                                      </span>
                                    );
                                  }
                                  return null;
                                })()}
                                {/* Conflict tags always shown */}
                                {critCount > 0 && (
                                  <span className="dc-tag" style={{ background: C.redBg, color: C.red, fontSize: 9 }}>
                                    {critCount} critical
                                  </span>
                                )}
                                {highCount > 0 && (
                                  <span className="dc-tag" style={{ background: C.orangeBg, color: C.orange, fontSize: 9 }}>
                                    {highCount} high
                                  </span>
                                )}
                                {medCount > 0 && (
                                  <span className="dc-tag" style={{ background: C.amberBg, color: C.amber, fontSize: 9 }}>
                                    {medCount} conflict{medCount > 1 ? 's' : ''}
                                  </span>
                                )}
                                {/* GC Clear tag for ready_for_review docs with zero open conflicts */}
                                {stage.id === 'ready_for_review' && realDocConflicts.length === 0 && doc.auditScore != null && (
                                  <span className="dc-tag" style={{ background: C.successBg, color: C.success, fontSize: 9 }}>
                                    GC Clear
                                  </span>
                                )}
                              </div>
                              {/* Dependency detail line for needs_generation */}
                              {isGenStage && (depInfo.blocks > 0 || depInfo.blockedBy > 0) && (
                                <div className="dc-dep-detail">
                                  {depInfo.blocks > 0 && (
                                    <span style={{ color: C.orange, fontSize: 11 }}>
                                      Needed by: {depInfo.blocksNames.slice(0, 3).join(', ')}{depInfo.blocksNames.length > 3 ? ` +${depInfo.blocksNames.length - 3} more` : ''}
                                    </span>
                                  )}
                                  {depInfo.blockedBy > 0 && (
                                    <span style={{ color: C.blue, fontSize: 11 }}>
                                      {depInfo.blocks > 0 ? ' \u00B7 ' : ''}Needs: {depInfo.blockedByNames.slice(0, 3).join(', ')}{depInfo.blockedByNames.length > 3 ? ` +${depInfo.blockedByNames.length - 3} more` : ''}
                                    </span>
                                  )}
                                </div>
                              )}
                              {/* Inline status chip when this doc is in the active job */}
                              {jobDocIds.has(doc.templateId) && (generating || generatingAll) && (() => {
                                const isActiveDoc = genProgress?.title === doc.title;
                                if (isActiveDoc) {
                                  const label = auditProgress?.phase === 'scoring'
                                    ? `Scoring${auditProgress?.score != null ? ` ${auditProgress.score}%` : '...'}`
                                    : auditProgress?.phase === 'fixing'
                                    ? `Fixing ${auditProgress?.attempt}/${auditProgress?.maxAttempts}`
                                    : `Generating${auditProgress ? ` ${auditProgress.attempt}/${auditProgress.maxAttempts}` : ''}`;
                                  const chipColor = auditProgress?.phase === 'scoring'
                                    ? (auditProgress?.score != null && auditProgress.score >= 85 ? C.success : C.amber)
                                    : C.accent;
                                  return (
                                    <span className="dc-progress-pulse" style={{
                                      fontSize: 10, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap',
                                      background: chipColor, padding: '2px 8px', borderRadius: 4,
                                    }}>
                                      {'\u25CF'} {label}
                                    </span>
                                  );
                                }
                                return (
                                  <span style={{
                                    fontSize: 10, fontWeight: 500, color: C.textMuted, whiteSpace: 'nowrap',
                                    background: C.borderLight, padding: '2px 8px', borderRadius: 4,
                                  }}>
                                    Queued
                                  </span>
                                );
                              })()}
                            </div>

                            <div className="dc-doc-meta">
                              {/* Generate button for not_started docs */}
                              {doc.status === 'not_started' && (
                                <button
                                  className="dc-btn-generate"
                                  onClick={(e) => { e.stopPropagation(); generateDocument(doc.id); }}
                                  disabled={generating || generatingAll}
                                  title="Generate this document"
                                >
                                  {generating ? '...' : 'Generate'}
                                </button>
                              )}

                              {/* Audit score + regen button */}
                              {(doc.auditScore != null || (jobDocIds.has(doc.templateId) && (generating || generatingAll))) && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                  {/* Score badge: blank while generating, score once done */}
                                  {jobDocIds.has(doc.templateId) && (generating || generatingAll) && doc.status !== 'draft' ? (
                                    <span
                                      className="dc-badge"
                                      style={{ color: C.textMuted, background: C.borderLight, minWidth: 36, textAlign: 'center' }}
                                    >
                                      ...
                                    </span>
                                  ) : preRegenScores[doc.templateId] != null && effectiveScore != null && preRegenScores[doc.templateId] !== effectiveScore ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                      <span
                                        className="dc-badge"
                                        style={{ color: C.textMuted, background: C.borderLight, textDecoration: 'line-through', fontSize: 10, opacity: 0.7 }}
                                      >
                                        {preRegenScores[doc.templateId]}%
                                      </span>
                                      <span style={{ color: C.textMuted, fontSize: 10 }}>{'\u2192'}</span>
                                      <span
                                        className="dc-badge"
                                        style={{
                                          color: effectiveScore >= 95 ? C.success : effectiveScore >= 80 ? C.amber : C.red,
                                          background: effectiveScore >= 95 ? C.successBg : effectiveScore >= 80 ? C.amberBg : C.redBg,
                                        }}
                                      >
                                        {effectiveScore}%
                                      </span>
                                    </span>
                                  ) : effectiveScore != null ? (
                                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                      <span
                                        className="dc-badge"
                                        style={{
                                          color: effectiveScore >= 95 ? C.success : effectiveScore >= 80 ? C.amber : C.red,
                                          background: effectiveScore >= 95 ? C.successBg : effectiveScore >= 80 ? C.amberBg : C.redBg,
                                        }}
                                      >
                                        {effectiveScore}%
                                      </span>
                                      {/* v5: Traffic light dot */}
                                      <span style={{
                                        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
                                        background: effectiveScore >= 90 ? C.success : effectiveScore >= 70 ? C.amber : C.red,
                                      }} title={effectiveScore >= 90 ? 'GREEN' : effectiveScore >= 70 ? 'YELLOW' : 'RED'} />
                                    </span>
                                  ) : null}
                                  {doc.content && !isAttyStage && (
                                    <button
                                      className="dc-btn-generate"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        // Protect attorney-reviewed docs from accidental regen
                                        if (REVIEWED_STATUSES.has(doc.status)) {
                                          if (!window.confirm(`"${doc.title}" is in ${STATUS_CONFIG[doc.status].label} status.\n\nRegenerating will reset it to DRAFT and require re-review. Continue?`)) return;
                                        }
                                        setDiagnosticDoc(doc);
                                      }}
                                      style={{
                                        fontSize: 10, padding: '2px 6px',
                                        background: (genProgress && genProgress.title === doc.title && (generating || generatingAll))
                                          ? C.accent : regenQueue.has(doc.id) ? C.blue : undefined,
                                        color: (genProgress && genProgress.title === doc.title && (generating || generatingAll))
                                          ? '#fff' : regenQueue.has(doc.id) ? '#fff' : undefined,
                                        borderColor: (genProgress && genProgress.title === doc.title && (generating || generatingAll))
                                          ? C.accent : regenQueue.has(doc.id) ? C.blue : undefined,
                                      }}
                                      title={generating || generatingAll
                                        ? (regenQueue.has(doc.id) ? 'Queued — will regen after current batch' : 'Queue for regen')
                                        : 'Regenerate this document'}
                                    >
                                      {genProgress && genProgress.title === doc.title && (generating || generatingAll)
                                        ? `${auditProgress?.phase === 'scoring' ? 'Scoring' : auditProgress?.phase === 'fixing' ? 'Fixing' : 'Generating'}...`
                                        : regenQueue.has(doc.id) ? 'Queued' : 'Regen'}
                                    </button>
                                  )}
                                </span>
                              )}

                              {/* Days in state (non-generation stages only) */}
                              {days > 0 && !isGenStage && (
                                <span className="dc-days" style={{ color: days >= 14 ? C.red : days >= 7 ? C.amber : C.textMuted }}>
                                  {days}d
                                </span>
                              )}

                              {/* Status badge */}
                              <span className="dc-badge" style={{ color: sc.color, background: sc.bg }}>
                                {sc.label}
                              </span>
                            </div>
                          </div>
                        );
                          })());
                          return elements;
                        });
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Conflicts Panel */}
      {libraryScore && libraryScore.conflicts.length > 0 && (() => {
        const realConflicts = libraryScore.conflicts.filter(c => c.status !== 'pending');
        const pendingConflicts = libraryScore.conflicts.filter(c => c.status === 'pending');
        return (
        <div className="dc-conflicts">
          <button
            className="dc-conflicts-header"
            onClick={() => setShowConflicts(!showConflicts)}
          >
            <span style={{ fontSize: 14 }}>{'\u26A0\uFE0F'}</span>
            <span className="dc-conflicts-title">
              Cross-Document Conflicts ({realConflicts.length})
              {pendingConflicts.length > 0 && (
                <span style={{ fontWeight: 400, color: C.textMuted, marginLeft: 6 }}>
                  + {pendingConflicts.length} pending verification
                </span>
              )}
            </span>
            <span className="dc-chevron">{showConflicts ? '\u25B2' : '\u25BC'}</span>
          </button>
          {showConflicts && realConflicts.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 16px', borderBottom: `1px solid ${C.borderLight}` }}>
              <button
                className="dc-btn dc-btn-secondary"
                onClick={() => fixAllConflicts(realConflicts)}
                disabled={fixingConflicts || generating}
                style={{ fontSize: 11, padding: '4px 12px', borderColor: C.blue, color: C.blue }}
              >
                {fixingConflicts ? 'Fixing...' : `AI Fix All (${realConflicts.filter(c => documents.some(d => d.templateId === c.documentA && d.content)).length})`}
              </button>
              {fixProgress && (
                <span style={{ fontSize: 11, color: C.textSec }}>
                  {fixProgress.current}/{fixProgress.total}: {fixProgress.title}
                </span>
              )}
            </div>
          )}

          {showConflicts && (
            <div className="dc-conflicts-body">
              {(['critical', 'high', 'medium', 'low'] as const).map(severity => {
                const items = realConflicts.filter(c => c.severity === severity);
                if (items.length === 0) return null;
                const cfg = SEVERITY_CONFIG[severity];
                return (
                  <div key={severity} className="dc-conflict-group">
                    <div className="dc-conflict-group-header" style={{ color: cfg.color }}>
                      {cfg.label} ({items.length})
                    </div>
                    {items.slice(0, 5).map(conflict => {
                      const hasDoc = documents.some(d => d.templateId === conflict.documentA && d.content);
                      return (
                      <div key={conflict.id} className="dc-conflict-item" style={{ borderLeftColor: cfg.color }}>
                        <div className="dc-conflict-type">
                          <span className="dc-badge" style={{ color: cfg.color, background: cfg.bg, fontSize: 10, padding: '1px 6px' }}>
                            {conflict.type.replace(/_/g, ' ')}
                          </span>
                          {conflict.documentA && (
                            <span style={{ fontSize: 11, color: C.textMuted }}>
                              {conflict.documentA.replace(/^\d+\.\d+-/, '').replace(/-/g, ' ')}
                            </span>
                          )}
                        </div>
                        <div className="dc-conflict-desc">{conflict.description}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                          <div className="dc-conflict-rec" style={{ flex: 1 }}>{conflict.recommendation}</div>
                          {hasDoc && (
                            <button
                              className="dc-btn dc-btn-secondary"
                              onClick={() => handleFixConflict(conflict)}
                              disabled={fixingConflicts || generating}
                              style={{
                                fontSize: 11, padding: '3px 10px', whiteSpace: 'nowrap',
                                borderColor: C.blue, color: C.blue, flexShrink: 0,
                              }}
                            >
                              AI Fix
                            </button>
                          )}
                        </div>
                      </div>
                      );
                    })}
                    {items.length > 5 && (
                      <div style={{ fontSize: 11, color: C.textMuted, padding: '4px 12px' }}>
                        +{items.length - 5} more {severity} conflict(s)
                      </div>
                    )}
                  </div>
                );
              })}
              {pendingConflicts.length > 0 && (
                <div className="dc-conflict-group">
                  <div className="dc-conflict-group-header" style={{ color: C.textMuted }}>
                    Pending Verification ({pendingConflicts.length})
                    <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 8 }}>
                      Will resolve as documents are generated
                    </span>
                  </div>
                  {pendingConflicts.slice(0, 3).map(conflict => (
                    <div key={conflict.id} className="dc-conflict-item" style={{ borderLeftColor: C.border, opacity: 0.7 }}>
                      <div className="dc-conflict-type">
                        <span className="dc-badge" style={{ color: C.textMuted, background: C.borderLight, fontSize: 10, padding: '1px 6px' }}>
                          {conflict.type.replace(/_/g, ' ')}
                        </span>
                      </div>
                      <div className="dc-conflict-desc">{conflict.description}</div>
                    </div>
                  ))}
                  {pendingConflicts.length > 3 && (
                    <div style={{ fontSize: 11, color: C.textMuted, padding: '4px 12px' }}>
                      +{pendingConflicts.length - 3} more pending
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
        );
      })()}

      {/* Regen Diagnostic Modal */}
      {diagnosticDoc && activeBrand && (
        <RegenDiagnosticModal
          doc={diagnosticDoc}
          config={activeBrand.config}
          onClose={() => setDiagnosticDoc(null)}
          onFixAndRegen={async (fixes) => {
            // Save fixes to Brand Config
            for (const [field, value] of Object.entries(fixes)) {
              if (value.trim()) {
                await onUpdateConfigField(field as keyof BrandConfig, value.trim());
              }
            }
            const docToRegen = diagnosticDoc;
            setDiagnosticDoc(null);
            if (generating || generatingAll) {
              // Busy — queue it (auto-processes when current batch finishes)
              setRegenQueue(prev => { const next = new Set(prev); next.add(docToRegen.id); return next; });
            } else {
              regenerateBatch([docToRegen]);
            }
          }}
          onRegenAnyway={() => {
            const docToRegen = diagnosticDoc;
            setDiagnosticDoc(null);
            if (generating || generatingAll) {
              setRegenQueue(prev => { const next = new Set(prev); next.add(docToRegen.id); return next; });
            } else {
              regenerateBatch([docToRegen]);
            }
          }}
        />
      )}

      {/* Pre-Flight Config Check Modal */}
      {preFlightGaps && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)',
        }} onClick={() => setPreFlightGaps(null)}>
          <div
            style={{
              background: C.bg, borderRadius: 4, padding: 24, width: 480,
              maxHeight: '80vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: `1px solid ${C.border}`,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              Missing Configuration
            </div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16 }}>
              The following fields are required for generation. Fill them in below — they'll be saved to your brand config.
            </div>
            {preFlightGaps.gaps.map(gap => (
              <div key={gap.field} style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.textSec, display: 'block', marginBottom: 2 }}>
                  {gap.label}
                </label>
                <div style={{ fontSize: 10, color: C.textMuted, marginBottom: 4 }}>
                  Needed by: {gap.templates.slice(0, 3).join(', ')}{gap.templates.length > 3 ? ` +${gap.templates.length - 3} more` : ''}
                </div>
                <input
                  type="text"
                  autoFocus={preFlightGaps.gaps[0].field === gap.field}
                  value={preFlightGaps.values[gap.field] || ''}
                  onChange={e => setPreFlightGaps(prev => prev ? {
                    ...prev,
                    values: { ...prev.values, [gap.field]: e.target.value },
                  } : null)}
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: 13, borderRadius: 6,
                    border: `1px solid ${C.border}`, outline: 'none', fontFamily: 'inherit',
                  }}
                  placeholder={`Enter ${gap.label.toLowerCase()}...`}
                />
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
              <button
                className="dc-btn dc-btn-secondary"
                onClick={() => setPreFlightGaps(null)}
                style={{ fontSize: 12, padding: '6px 16px' }}
              >
                Cancel
              </button>
              <button
                className="dc-btn"
                onClick={async () => {
                  if (!preFlightGaps) return;
                  const { values, pendingAction } = preFlightGaps;
                  setPreFlightGaps(null);
                  // Save all filled values to brand config
                  for (const [field, value] of Object.entries(values)) {
                    if (value.trim()) {
                      await onUpdateConfigField(field as keyof BrandConfig, value.trim());
                    }
                  }
                  pendingAction();
                }}
                disabled={preFlightGaps.gaps.some(g => !(preFlightGaps.values[g.field] || '').trim())}
                style={{
                  fontSize: 12, padding: '6px 16px',
                  background: C.btn, color: '#fff', borderRadius: 3,
                  opacity: preFlightGaps.gaps.some(g => !(preFlightGaps.values[g.field] || '').trim()) ? 0.5 : 1,
                }}
              >
                Save & Generate
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Config Value Prompt Modal */}
      {configPrompt && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: 'rgba(0,0,0,0.4)',
        }} onClick={() => setConfigPrompt(null)}>
          <div
            style={{
              background: C.bg, borderRadius: 4, padding: 24, width: 420,
              boxShadow: '0 20px 60px rgba(0,0,0,0.15)', border: `1px solid ${C.border}`,
            }}
            onClick={e => e.stopPropagation()}
          >
            <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4 }}>
              Confirm Entity Value
            </div>
            <div style={{ fontSize: 12, color: C.textSec, marginBottom: 16 }}>
              {configPrompt.value
                ? <>Confirm or update <strong>{configPrompt.label}</strong> before AI fixes this conflict. The value will be saved to your brand config.</>
                : <>This conflict references <strong>{configPrompt.label}</strong> which is empty in your brand config. Enter the value below — it will be saved and used for the AI fix.</>
              }
            </div>
            <label style={{ fontSize: 12, fontWeight: 600, color: C.textSec, display: 'block', marginBottom: 4 }}>
              {configPrompt.label}
            </label>
            <input
              type="text"
              autoFocus
              value={configPrompt.value}
              onChange={e => setConfigPrompt({ ...configPrompt, value: e.target.value })}
              onKeyDown={e => { if (e.key === 'Enter' && configPrompt.value.trim()) handleConfigSubmit(); }}
              style={{
                width: '100%', padding: '8px 12px', fontSize: 14, borderRadius: 3,
                border: `1px solid ${C.border}`, outline: 'none', marginBottom: 16,
                fontFamily: 'inherit',
              }}
              placeholder={`Enter ${configPrompt.label.toLowerCase()}...`}
            />
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                className="dc-btn dc-btn-secondary"
                onClick={() => setConfigPrompt(null)}
                style={{ fontSize: 12, padding: '6px 16px' }}
              >
                Cancel
              </button>
              <button
                className="dc-btn"
                onClick={handleConfigSubmit}
                disabled={!configPrompt.value.trim()}
                style={{
                  fontSize: 12, padding: '6px 16px',
                  background: C.btn, color: '#fff', borderRadius: 3,
                  opacity: configPrompt.value.trim() ? 1 : 0.5,
                }}
              >
                Save & Fix
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
